import { PartnerType } from './partner';

// Keep in sync with dentallink .../common/model/UserRole.java
// The backend serialises UserRole as the bare enum NAME ("TECH_MANAGER"), not an object.
export enum UserRole {
  ADMIN = 'ADMIN',
  DENTIST = 'DENTIST',
  DENTIST_WORKER = 'DENTIST_WORKER',
  DENTIST_GUEST = 'DENTIST_GUEST',
  TECH = 'TECH',
  TECH_MANAGER = 'TECH_MANAGER',
  TECH_WORKER = 'TECH_WORKER',
  COURIER = 'COURIER',
  VIEWER = 'VIEWER',
  SUPPLIER = 'SUPPLIER'
}

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.ADMIN]: 'Admin',
  [UserRole.DENTIST]: 'Dentist',
  [UserRole.DENTIST_WORKER]: 'Staff Dentist',
  [UserRole.DENTIST_GUEST]: 'Guest Dentist',
  [UserRole.TECH]: 'Tech',
  [UserRole.TECH_MANAGER]: 'Tech Manager',
  [UserRole.TECH_WORKER]: 'Tech Worker',
  [UserRole.COURIER]: 'Courier',
  [UserRole.VIEWER]: 'Viewer',
  [UserRole.SUPPLIER]: 'Supplier'
};

// Keep in sync with dentallink .../user/api/dto/AdminUserDTO.LastActiveSource
export enum LastActiveSource {
  ACTIVE_SESSION = 'ACTIVE_SESSION',
  OFFLINE_SESSION = 'OFFLINE_SESSION',
  LOGIN_EVENT = 'LOGIN_EVENT'
}

// Keep in sync with dentallink .../user/api/dto/AdminUserDTO.java
export interface AdminUserDTO {
  id: string;
  name: string;
  email?: string | null;
  // What they are in the tenant being listed, and what the API will actually enforce: the
  // membership row's role where there is one, the Keycloak account's otherwise.
  role?: UserRole | null;
  // The role recorded in tenant_memberships. Null means no row - they are still living off their
  // account's role, and will lose access to this tenant when KEYCLOAK_ROLE_FALLBACK is switched off.
  membershipRole?: UserRole | null;
  // Whether the Keycloak account may sign in at all — realm-wide, unlike membershipActive.
  enabled: boolean;
  // Whether their membership of the tenant being listed is live. False for somebody let go here who
  // goes on working elsewhere; null for a membership that predates the tenant_memberships table.
  membershipActive?: boolean | null;
  createdAt?: string | null;
  // When the user was last seen working — the newest token refresh on any of their sessions,
  // not when they signed in. Null means nothing visible: never logged in, or no data source.
  lastActiveAt?: string | null;
  lastActiveSource?: LastActiveSource | null;
}

// Keep in sync with dentallink .../user/api/dto/AdminUserCreateDTO.java
// There is no username field: the account's Keycloak username is its email.
export interface AdminUserCreateRequest {
  tenantId: number;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  // Adds an account that already exists to this tenant instead of failing with
  // USER_ALREADY_EXISTS. Set only after the admin has been asked: a taken email is usually a typo,
  // and the alternative is pulling a stranger into somebody's organisation.
  attachExisting?: boolean;
}

// POST /v1/admin/users. temporaryPassword is generated server-side and returned exactly once — it
// is not stored and cannot be read back, so the UI has to surface it immediately.
export interface AdminUserCreatedResponse {
  user: AdminUserDTO;
  // Null when existingAccount is true — that person keeps the password they already have.
  temporaryPassword: string | null;
  // True when no account was created: an existing one was added to this tenant.
  existingAccount?: boolean;
}

// Roles a hub admin may assign when provisioning a user for a tenant of the given type. ADMIN is
// never among them — system admins are not created through a tenant's user list.
// Keep in sync with dentallink UserService.rolesOf(PartnerType), which rejects anything else with
// a 400; offering only these keeps the picker from listing roles the backend will refuse.
export function assignableRolesFor(type: PartnerType | null | undefined): UserRole[] {
  switch (type) {
    case PartnerType.TECH:
      return [UserRole.TECH, UserRole.TECH_MANAGER, UserRole.TECH_WORKER, UserRole.VIEWER];
    case PartnerType.DENTIST:
      return [UserRole.DENTIST, UserRole.DENTIST_WORKER, UserRole.DENTIST_GUEST, UserRole.VIEWER];
    case PartnerType.SUPPLIER:
      return [UserRole.SUPPLIER];
    case PartnerType.COURIER:
      return [UserRole.COURIER];
    default:
      return [];
  }
}

// GET /v1/admin/users returns { data, size } (backend common.dto.CollectionDTO). Note this is a
// DIFFERENT wrapper from the feature-flags one, which is { items, totalCount } (backend
// common.api.CollectionDTO) — the backend has two classes by that name. Neither matches the
// app's PaginatedCollection { data, size, total }.
export interface AdminUserCollection {
  data: AdminUserDTO[];
  size: number;
}

// Keep in sync with dentallink .../user/api/dto/InvitationDTO.java
// What became of an invitation; only PENDING can be re-sent or withdrawn.
export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED';

// An invitation a tenant has sent. Carries the address and nothing else about the person: until
// they accept they are not a member, and their name is not the tenant's - or the console's - to show.
export interface InvitationDTO {
  id: number;
  email: string | null;
  role: UserRole;
  status: InvitationStatus;
  sentAt?: string | null;
  expiresAt: string;
}

// GET /v1/admin/users/invitations returns the same { data, size } wrapper as the user list.
export interface InvitationCollection {
  data: InvitationDTO[];
  size: number;
}
